// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { InvoiceDetail } from '@/billing/api/types';
import { useVoidInvoice } from '@/billing/hooks/use-void-invoice';
import { Warning as WarningIcon } from '@/constants/hardfin-standard-icons';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const voidInvoiceFormSchema = z.object({
  reason: z.string().trim().min(1, 'A reason is required to void an invoice'),
});

type VoidInvoiceFormData = z.infer<typeof voidInvoiceFormSchema>;

// Backend codes that reflect a state conflict (the invoice is already in a
// state that can't or needn't be voided again). Closing the dialog is
// correct — retrying won't change the outcome and the parent surfaces a
// known-error snackbar with the reason.
const KNOWN_STATUS_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_already_void,
  ERROR_CODE_MAPPING.invoice_not_voidable,
  ERROR_CODE_MAPPING.invoice_has_settlements,
]);

interface Props {
  isOpen: boolean;
  invoice: InvoiceDetail;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

function DetailRow(detailProps: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="baseline">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 72 }}
      >
        {detailProps.label}
      </Typography>
      <Typography variant="body2">{detailProps.value}</Typography>
    </Stack>
  );
}

export function VoidInvoiceDialog(props: Props) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VoidInvoiceFormData>({
    resolver: zodResolver(voidInvoiceFormSchema),
    defaultValues: { reason: '' },
  });

  // Reset the form each time the dialog transitions from closed → open so
  // users always see a clean state, even after a successful void earlier.
  useEffect(() => {
    if (props.isOpen) {
      reset({ reason: '' });
    }
  }, [props.isOpen, reset]);

  const voidMutation = useVoidInvoice({
    invoiceKey: props.invoice.key,
    onSuccess: () => {
      props.onClose();
      props.onSuccess?.();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      // 404 (invoice gone) or a known status conflict (e.g. already void)
      // closes the dialog — retrying won't help. The parent navigates
      // away on 404 and shows the known-error snackbar for conflicts.
      if (httpStatusCode === 404 || KNOWN_STATUS_CODES.has(apiStatusCode)) {
        props.onClose();
        props.onError?.(message, apiStatusCode, httpStatusCode);
        return;
      }
      // Unexpected errors (5xx / unknown) keep the dialog open so the
      // user can retry without re-entering the reason.
      props.onError?.(message, apiStatusCode, httpStatusCode);
    },
  });

  const close = () => {
    if (voidMutation.isPending) {
      return;
    }
    props.onClose();
  };

  const onSubmit = (data: VoidInvoiceFormData) => {
    voidMutation.mutate({
      invoiceKey: props.invoice.key,
      voidReason: data.reason.trim(),
    });
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={close}>
      <DialogTitle
        sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
      >
        Void invoice
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ py: 3 }}>
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="medium" />}
            sx={{ marginY: 2, alignItems: 'center' }}
          >
            <AlertTitle variant="h6">
              Void invoice <strong>{props.invoice.invoiceNumber}</strong>?
            </AlertTitle>
            <Typography variant="body1">
              This invoice will be marked as void. All charges will return to
              billable status and can be included in a future invoice
            </Typography>
          </Alert>

          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack spacing={0.75}>
              <DetailRow label="Invoice" value={props.invoice.invoiceNumber} />
              {props.invoice.customerName && (
                <DetailRow
                  label="Customer"
                  value={props.invoice.customerName}
                />
              )}
              <DetailRow
                label="Total"
                value={formatCurrencyValue(props.invoice.total)}
              />
            </Stack>
          </Box>

          <TextField
            label="Reason"
            placeholder="Why is this invoice being voided?"
            disabled={voidMutation.isPending}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            error={!!errors.reason}
            helperText={errors.reason?.message}
            sx={{ mt: 1 }}
            {...register('reason')}
          />
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Button onClick={close} disabled={voidMutation.isPending}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            color="error"
            loading={voidMutation.isPending}
          >
            Void invoice
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
